// src/pages/Social.jsx
// Módulo Social Publisher — imágenes desde Google Drive (carpeta "Devit Media")

import { useState, useEffect, useCallback } from 'react';

const API = 'https://bot.devit.es/api';
function getToken() { return localStorage.getItem('devit_token'); }
function authHeaders() {
  return { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' };
}

// ─── UI base ────────────────────────────────────────────────────────────────

function Badge({ children, color = 'green' }) {
  const colors = {
    green:  { bg: '#1a3a2a', text: '#4ade80', border: '#2a5a3a' },
    red:    { bg: '#3a1a1a', text: '#f87171', border: '#5a2a2a' },
    blue:   { bg: '#1a2a3a', text: '#60a5fa', border: '#2a3a5a' },
    yellow: { bg: '#3a2e1a', text: '#fbbf24', border: '#5a4a2a' },
    gray:   { bg: '#2a2e4a', text: '#9b9fc4', border: '#3a3e5a' },
    purple: { bg: '#2e1a3a', text: '#c4b5fd', border: '#4a2a5a' },
  };
  const c = colors[color] || colors.gray;
  return (
    <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`,
      borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
      {children}
    </span>
  );
}

function Btn({ children, onClick, variant = 'primary', disabled = false, style = {} }) {
  const styles = {
    primary:   { background: '#7c3aed', color: '#fff', border: 'none' },
    secondary: { background: '#1a1d2e', color: '#9b9fc4', border: '1px solid #2a2e4a' },
    danger:    { background: '#7f1d1d', color: '#fca5a5', border: 'none' },
    ghost:     { background: 'transparent', color: '#9b9fc4', border: '1px solid #2a2e4a' },
    teal:      { background: '#0f3a35', color: '#2dd4bf', border: '1px solid #1a5a50' },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...styles[variant], padding: '8px 18px', borderRadius: 8,
      cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600,
      opacity: disabled ? 0.5 : 1, transition: 'opacity 0.15s', ...style,
    }}>
      {children}
    </button>
  );
}

function Card({ children, style = {} }) {
  return (
    <div style={{ background: '#1a1d2e', border: '1px solid #2a2e4a',
      borderRadius: 12, padding: 24, ...style }}>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, placeholder, type = 'text', rows }) {
  const base = {
    width: '100%', background: '#12141f', border: '1px solid #2a2e4a',
    borderRadius: 8, padding: '10px 14px', color: '#f0f0ff', fontSize: 14,
    outline: 'none', boxSizing: 'border-box',
  };
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ display: 'block', color: '#9b9fc4', fontSize: 13, marginBottom: 6 }}>{label}</label>}
      {rows
        ? <textarea value={value} onChange={e => onChange(e.target.value)}
            placeholder={placeholder} rows={rows} style={{ ...base, resize: 'vertical' }} />
        : <input type={type} value={value} onChange={e => onChange(e.target.value)}
            placeholder={placeholder} style={base} />
      }
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ display: 'block', color: '#9b9fc4', fontSize: 13, marginBottom: 6 }}>{label}</label>}
      <select value={value} onChange={e => onChange(e.target.value)} style={{
        width: '100%', background: '#12141f', border: '1px solid #2a2e4a',
        borderRadius: 8, padding: '10px 14px', color: '#f0f0ff', fontSize: 14,
      }}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function PlatformToggle({ selected, onChange }) {
  const platforms = [
    { id: 'linkedin', label: 'LinkedIn', icon: '💼' },
    { id: 'facebook', label: 'Facebook', icon: '📘' },
    { id: 'instagram', label: 'Instagram', icon: '📸' },
  ];
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', color: '#9b9fc4', fontSize: 13, marginBottom: 8 }}>Plataformas</label>
      <div style={{ display: 'flex', gap: 10 }}>
        {platforms.map(p => {
          const active = selected.includes(p.id);
          return (
            <button key={p.id} onClick={() => {
              if (active) onChange(selected.filter(x => x !== p.id));
              else onChange([...selected, p.id]);
            }} style={{
              flex: 1, padding: '10px 0', borderRadius: 8, cursor: 'pointer',
              fontWeight: 600, fontSize: 13,
              background: active ? '#3b0764' : '#12141f',
              border: `1px solid ${active ? '#7c3aed' : '#2a2e4a'}`,
              color: active ? '#e9d5ff' : '#9b9fc4', transition: 'all 0.15s',
            }}>
              {p.icon} {p.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DriveImageInput({ value, onChange, plataformas, driveConectado, driveCarpeta }) {
  const tieneInstagram = plataformas.includes('instagram');
  const necesitaImagen = tieneInstagram;

  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', color: '#9b9fc4', fontSize: 13, marginBottom: 6 }}>
        ☁️ Imagen desde Google Drive
        {tieneInstagram && (
          <span style={{ marginLeft: 8, color: '#f87171', fontSize: 11, fontWeight: 600 }}>
            ⚠ Obligatorio para Instagram
          </span>
        )}
        {!tieneInstagram && (
          <span style={{ marginLeft: 8, color: '#5a5e82', fontSize: 11 }}>
            Opcional para LinkedIn y Facebook
          </span>
        )}
      </label>
      {!driveConectado ? (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 8,
          background: '#2a1a0a', border: '1px solid #5a3a1a', color: '#fbbf24', fontSize: 12 }}>
          ⚠️ Google Drive no conectado — ve a <strong>Credenciales → Google Drive</strong>.
        </div>
      ) : !driveCarpeta ? (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 8,
          background: '#1a2a1a', border: '1px solid #2a4a2a', color: '#86efac', fontSize: 12 }}>
          ✓ Drive conectado — crea una carpeta <strong>"Devit Media"</strong> y sube tus imágenes.
        </div>
      ) : (
        <div style={{ padding: '8px 14px', borderRadius: 8, marginBottom: 8,
          background: '#1a2a1a', border: '1px solid #2a4a2a', color: '#86efac', fontSize: 12 }}>
          ✓ Drive conectado — carpeta <strong>"Devit Media"</strong> lista
        </div>
      )}
      <input type="text" value={value} onChange={e => onChange(e.target.value)}
        placeholder={driveConectado ? 'Nombre del archivo en Devit Media — ej: banner_mayo.jpg' : 'Conecta Drive primero'}
        disabled={!driveConectado}
        style={{
          width: '100%', background: driveConectado ? '#12141f' : '#0a0c14',
          border: `1px solid ${necesitaImagen && !value && driveConectado ? '#5a2a2a' : '#2a2e4a'}`,
          borderRadius: 8, padding: '10px 14px', color: '#f0f0ff', fontSize: 14,
          outline: 'none', boxSizing: 'border-box',
          opacity: driveConectado ? 1 : 0.5, cursor: driveConectado ? 'text' : 'not-allowed',
        }}
      />
    </div>
  );
}

// ─── Tab: Generar + Publicar ─────────────────────────────────────────────────

function TabGenerar({ status }) {
  const [tema, setTema]               = useState('');
  const [plataforma, setPlataforma]   = useState('linkedin');
  const [tono, setTono]               = useState('profesional');
  const [contenido, setContenido]     = useState('');
  const [plataformas, setPlataformas] = useState(['linkedin']);
  const [imagenNombre, setImagenNombre] = useState('');
  const [loading, setLoading]         = useState(false);
  const [publishing, setPublishing]   = useState(false);
  const [msg, setMsg]                 = useState(null);

  async function handleGenerar() {
    if (!tema.trim()) return;
    setLoading(true); setMsg(null);
    try {
      const r = await fetch(`${API}/social/generate`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ tema, plataforma, tono }),
      });
      const d = await r.json();
      if (r.ok) {
        setContenido(d.contenido);
        setMsg({ type: 'ok', text: `✅ Contenido generado (${d.tokens_used} tokens)` });
      } else {
        setMsg({ type: 'err', text: d.detail || 'Error generando contenido' });
      }
    } catch { setMsg({ type: 'err', text: 'Error de conexión' }); }
    setLoading(false);
  }

  async function handlePublicar() {
    if (!contenido.trim() || plataformas.length === 0) return;
    setPublishing(true); setMsg(null);
    try {
      const r = await fetch(`${API}/social/publish`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ contenido, plataformas, imagen_nombre: imagenNombre.trim() || null }),
      });
      const d = await r.json();
      if (r.ok) setMsg({ type: 'ok', text: `✅ Publicado en ${d.publicados}/${d.total} plataformas` });
      else setMsg({ type: 'err', text: d.detail || 'Error publicando' });
    } catch { setMsg({ type: 'err', text: 'Error de conexión' }); }
    setPublishing(false);
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
      <Card>
        <h3 style={{ color: '#f0f0ff', marginBottom: 20, fontSize: 16 }}>🤖 Generar contenido</h3>
        <Input label="Tema del post" value={tema} onChange={setTema}
          placeholder="Ej: Ventajas de usar IA en atención al cliente" />
        <Select label="Plataforma de generación" value={plataforma} onChange={setPlataforma}
          options={[
            { value: 'linkedin',  label: '💼 LinkedIn' },
            { value: 'facebook',  label: '📘 Facebook' },
            { value: 'instagram', label: '📸 Instagram' },
          ]} />
        <Select label="Tono" value={tono} onChange={setTono}
          options={[
            { value: 'profesional', label: 'Profesional' },
            { value: 'inspirador',  label: 'Inspirador'  },
            { value: 'educativo',   label: 'Educativo'   },
            { value: 'casual',      label: 'Casual'      },
            { value: 'provocador',  label: 'Provocador'  },
          ]} />
        <Btn onClick={handleGenerar} disabled={loading || !tema.trim()}>
          {loading ? '⏳ Generando...' : '✨ Generar post'}
        </Btn>
      </Card>

      <Card>
        <h3 style={{ color: '#f0f0ff', marginBottom: 20, fontSize: 16 }}>📤 Publicar</h3>
        <Input label="Contenido del post" value={contenido} onChange={setContenido}
          placeholder="Escribe o genera el contenido aquí..." rows={6} />
        <DriveImageInput value={imagenNombre} onChange={setImagenNombre}
          plataformas={plataformas} driveConectado={status?.drive_conectado} driveCarpeta={status?.drive_carpeta} />
        <PlatformToggle selected={plataformas} onChange={setPlataformas} />
        {msg && (
          <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
            background: msg.type === 'ok' ? '#1a3a2a' : '#3a1a1a',
            color: msg.type === 'ok' ? '#4ade80' : '#f87171',
            border: `1px solid ${msg.type === 'ok' ? '#2a5a3a' : '#5a2a2a'}` }}>
            {msg.text}
          </div>
        )}
        <Btn onClick={handlePublicar} disabled={publishing || !contenido.trim() || plataformas.length === 0}>
          {publishing ? '⏳ Publicando...' : '🚀 Publicar ahora'}
        </Btn>
      </Card>
    </div>
  );
}

// ─── Tab: Programar ──────────────────────────────────────────────────────────

function TabProgramar({ status }) {
  const [contenido, setContenido]       = useState('');
  const [plataformas, setPlataformas]   = useState(['linkedin']);
  const [imagenNombre, setImagenNombre] = useState('');
  const [fecha, setFecha]               = useState('');
  const [loading, setLoading]           = useState(false);
  const [msg, setMsg]                   = useState(null);
  const [scheduled, setScheduled]       = useState([]);
  const [loadingList, setLoadingList]   = useState(true);

  const cargarProgramados = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await fetch(`${API}/social/scheduled?estado=pendiente`, { headers: authHeaders() });
      if (r.ok) setScheduled(await r.json());
    } catch {}
    setLoadingList(false);
  }, []);

  useEffect(() => { cargarProgramados(); }, [cargarProgramados]);

  async function handleProgramar() {
    if (!contenido.trim() || !fecha || plataformas.length === 0) return;
    setLoading(true); setMsg(null);
    try {
      const r = await fetch(`${API}/social/schedule`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          contenido, plataformas,
          imagen_nombre: imagenNombre.trim() || null,
          programado_para: new Date(fecha).toISOString(),
        }),
      });
      const d = await r.json();
      if (r.ok) {
        setMsg({ type: 'ok', text: '✅ Post programado correctamente' });
        setContenido(''); setFecha(''); setImagenNombre('');
        cargarProgramados();
      } else {
        setMsg({ type: 'err', text: d.detail || 'Error programando post' });
      }
    } catch { setMsg({ type: 'err', text: 'Error de conexión' }); }
    setLoading(false);
  }

  async function handleCancelar(id) {
    try {
      await fetch(`${API}/social/scheduled/${id}`, { method: 'DELETE', headers: authHeaders() });
      cargarProgramados();
    } catch {}
  }

  const platformIcons = { linkedin: '💼', facebook: '📘', instagram: '📸' };

  return (
    <div>
      <Card style={{ marginBottom: 20 }}>
        <h3 style={{ color: '#f0f0ff', marginBottom: 20, fontSize: 16 }}>🗓️ Programar publicación</h3>
        <Input label="Contenido del post" value={contenido} onChange={setContenido}
          placeholder="Escribe el contenido a publicar..." rows={5} />
        <DriveImageInput value={imagenNombre} onChange={setImagenNombre}
          plataformas={plataformas} driveConectado={status?.drive_conectado} driveCarpeta={status?.drive_carpeta} />
        <PlatformToggle selected={plataformas} onChange={setPlataformas} />
        <Input label="Fecha y hora de publicación" value={fecha} onChange={setFecha} type="datetime-local" />
        {msg && (
          <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
            background: msg.type === 'ok' ? '#1a3a2a' : '#3a1a1a',
            color: msg.type === 'ok' ? '#4ade80' : '#f87171',
            border: `1px solid ${msg.type === 'ok' ? '#2a5a3a' : '#5a2a2a'}` }}>
            {msg.text}
          </div>
        )}
        <Btn onClick={handleProgramar}
          disabled={loading || !contenido.trim() || !fecha || plataformas.length === 0}>
          {loading ? '⏳ Programando...' : '📅 Programar'}
        </Btn>
      </Card>

      <Card>
        <h3 style={{ color: '#f0f0ff', marginBottom: 16, fontSize: 16 }}>⏳ Programaciones pendientes</h3>
        {loadingList ? (
          <p style={{ color: '#5a5e82' }}>Cargando...</p>
        ) : scheduled.length === 0 ? (
          <p style={{ color: '#5a5e82', fontSize: 14 }}>No hay posts programados pendientes.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {scheduled.map(s => (
              <div key={s.id} style={{ background: '#12141f', border: '1px solid #2a2e4a',
                borderRadius: 8, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    {s.plataformas.map(p => <Badge key={p} color="blue">{platformIcons[p]} {p}</Badge>)}
                    {s.imagen_nombre && <Badge color="gray">☁️ {s.imagen_nombre}</Badge>}
                  </div>
                  <p style={{ color: '#f0f0ff', fontSize: 13, marginBottom: 6 }}>{s.contenido}</p>
                  <p style={{ color: '#5a5e82', fontSize: 12 }}>
                    📅 {new Date(s.programado_para).toLocaleString('es-ES')}
                  </p>
                </div>
                <Btn variant="danger" onClick={() => handleCancelar(s.id)} style={{ marginLeft: 16, flexShrink: 0 }}>
                  Cancelar
                </Btn>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Tab: Historial ──────────────────────────────────────────────────────────

function TabHistorial() {
  const [posts, setPosts]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro]   = useState('');

  useEffect(() => {
    fetch(`${API}/social/history?limit=100`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(setPosts)
      .finally(() => setLoading(false));
  }, []);

  const filtrados = filtro ? posts.filter(p => p.plataforma === filtro) : posts;
  const platformIcons = { linkedin: '💼', facebook: '📘', instagram: '📸' };
  const estadoColor   = { publicado: 'green', fallido: 'red', borrador: 'gray' };

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ color: '#f0f0ff', fontSize: 16 }}>📋 Historial de posts</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {['', 'linkedin', 'facebook', 'instagram'].map(p => (
            <Btn key={p} variant={filtro === p ? 'primary' : 'ghost'} onClick={() => setFiltro(p)}>
              {p ? `${platformIcons[p]} ${p}` : 'Todos'}
            </Btn>
          ))}
        </div>
      </div>
      {loading ? (
        <p style={{ color: '#5a5e82' }}>Cargando...</p>
      ) : filtrados.length === 0 ? (
        <p style={{ color: '#5a5e82', fontSize: 14 }}>Sin historial de posts aún.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtrados.map(p => (
            <div key={p.id} style={{ background: '#12141f', border: '1px solid #2a2e4a', borderRadius: 8, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Badge color="blue">{platformIcons[p.plataforma]} {p.plataforma}</Badge>
                  <Badge color={estadoColor[p.estado] || 'gray'}>{p.estado}</Badge>
                  {p.fuente !== 'manual' && <Badge color="yellow">{p.fuente}</Badge>}
                </div>
                <span style={{ color: '#5a5e82', fontSize: 12 }}>
                  {new Date(p.created_at).toLocaleString('es-ES')}
                </span>
              </div>
              <p style={{ color: '#f0f0ff', fontSize: 13, marginBottom: p.error_msg ? 6 : 0 }}>{p.contenido}</p>
              {p.error_msg && <p style={{ color: '#f87171', fontSize: 12, marginTop: 6 }}>⚠️ {p.error_msg}</p>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─── Tab: Credenciales ───────────────────────────────────────────────────────

function TabCredenciales({ status, onRefresh }) {
  const [form, setForm] = useState({
    linkedin_token: '', linkedin_org_id: '',
    facebook_token: '', facebook_page_id: '', instagram_account_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState(null);

  function setField(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function handleSave() {
    setSaving(true); setMsg(null);
    const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v.trim() !== ''));
    try {
      const r = await fetch(`${API}/social/credentials`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (r.ok) {
        setMsg({ type: 'ok', text: '✅ Credenciales guardadas correctamente' });
        setForm({ linkedin_token: '', linkedin_org_id: '', facebook_token: '', facebook_page_id: '', instagram_account_id: '' });
        onRefresh();
      } else {
        setMsg({ type: 'err', text: d.detail || 'Error guardando' });
      }
    } catch { setMsg({ type: 'err', text: 'Error de conexión' }); }
    setSaving(false);
  }

  const netCard = (title, icon, connected, fields) => (
    <div style={{ background: '#12141f', border: `1px solid ${connected ? '#2a5a3a' : '#2a2e4a'}`,
      borderRadius: 10, padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ color: '#f0f0ff', fontWeight: 700, fontSize: 15 }}>{icon} {title}</span>
        <Badge color={connected ? 'green' : 'gray'}>{connected ? '✓ Conectado' : 'No conectado'}</Badge>
      </div>
      {fields}
    </div>
  );

  return (
    <div>
      <Card style={{ marginBottom: 20 }}>
        <h3 style={{ color: '#f0f0ff', marginBottom: 6, fontSize: 16 }}>🔑 Credenciales de Redes Sociales</h3>
        <p style={{ color: '#5a5e82', fontSize: 13, marginBottom: 24 }}>
          Los tokens se guardan de forma segura. Introduce solo los campos que quieras actualizar.
        </p>
        {netCard('LinkedIn', '💼', status?.linkedin_conectado, <>
          <Input label="Access Token" value={form.linkedin_token}
            onChange={v => setField('linkedin_token', v)} placeholder="Token de acceso de LinkedIn" type="password" />
          <Input label="Organization URN (opcional — para publicar como empresa)"
            value={form.linkedin_org_id} onChange={v => setField('linkedin_org_id', v)}
            placeholder="urn:li:organization:12345678" />
        </>)}
        {netCard('Facebook', '📘', status?.facebook_conectado, <>
          <Input label="Page Access Token" value={form.facebook_token}
            onChange={v => setField('facebook_token', v)} placeholder="Token de acceso de página" type="password" />
          <Input label="Page ID" value={form.facebook_page_id}
            onChange={v => setField('facebook_page_id', v)} placeholder="ID numérico de tu página" />
        </>)}
        {netCard('Instagram', '📸', status?.instagram_conectado, <>
          <p style={{ color: '#5a5e82', fontSize: 12, marginBottom: 12 }}>
            Instagram usa el mismo Page Access Token que Facebook.
          </p>
          <Input label="Instagram Business Account ID" value={form.instagram_account_id}
            onChange={v => setField('instagram_account_id', v)} placeholder="ID de cuenta Instagram Business" />
        </>)}
        {msg && (
          <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
            background: msg.type === 'ok' ? '#1a3a2a' : '#3a1a1a',
            color: msg.type === 'ok' ? '#4ade80' : '#f87171',
            border: `1px solid ${msg.type === 'ok' ? '#2a5a3a' : '#5a2a2a'}` }}>
            {msg.text}
          </div>
        )}
        <Btn onClick={handleSave} disabled={saving}>
          {saving ? '⏳ Guardando...' : '💾 Guardar credenciales'}
        </Btn>
      </Card>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ color: '#f0f0ff', fontSize: 16, marginBottom: 6 }}>☁️ Google Drive</h3>
            <p style={{ color: '#5a5e82', fontSize: 13 }}>
              Conecta tu Drive para publicar imágenes desde tu carpeta <strong style={{ color: '#9b9fc4' }}>Devit Media</strong>.
            </p>
          </div>
          <Badge color={status?.drive_conectado ? 'green' : 'gray'}>
            {status?.drive_conectado ? `✓ Conectado${status.drive_carpeta ? ' + Carpeta' : ''}` : 'No conectado'}
          </Badge>
        </div>
        <div style={{ marginTop: 16 }}>
          <Btn onClick={async () => {
            const r = await fetch(`${API}/drive/connect?authorization=Bearer ${getToken()}`);
            const d = await r.json();
            if (d.auth_url) window.open(d.auth_url, '_blank');
          }} variant={status?.drive_conectado ? 'secondary' : 'primary'}>
            {status?.drive_conectado ? '🔄 Reconectar Drive' : '🔗 Conectar Google Drive'}
          </Btn>
        </div>
      </Card>
    </div>
  );
}

// ─── Tab: Plan de Contenido ──────────────────────────────────────────────────

const MESES = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'
];
const OBJETIVO_COLOR = { awareness:'blue', lead:'purple', venta:'green', confianza:'yellow', educacion:'gray' };
const RED_ICON = { linkedin:'💼', facebook:'📘', instagram:'📸' };

function ModalEditarBorrador({ post, onClose, onSave }) {
  const [contenido,     setContenido]     = useState(post.contenido || '');
  const [titulo,        setTitulo]        = useState(post.titulo    || '');
  const [imagenFuente,  setImagenFuente]  = useState(post.imagen_fuente || 'pexels');
  const [imagenNombre,  setImagenNombre]  = useState(post.imagen_nombre || '');
  const [imagenPrompt,  setImagenPrompt]  = useState(post.imagen_prompt || '');
  const [fecha,         setFecha]         = useState(() => {
    const d = new Date(post.programado_para);
    return d.toISOString().slice(0,16);
  });
  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState(null);

  async function handleSave(aprobar = false) {
    setSaving(true); setMsg(null);
    const payload = { contenido, titulo, imagen_fuente: imagenFuente, imagen_nombre: imagenNombre || null,
                      imagen_prompt: imagenPrompt || null, programado_para: new Date(fecha).toISOString() };
    try {
      const r = await fetch(`${API}/social/scheduled/${post.id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload),
      });
      if (!r.ok) { const d = await r.json(); setMsg({ type:'err', text: d.detail || 'Error guardando' }); setSaving(false); return; }
      if (aprobar) {
        const r2 = await fetch(`${API}/social/scheduled/${post.id}/approve`, { method:'POST', headers: authHeaders() });
        const d2 = await r2.json();
        if (d2.fal_sin_credito) setMsg({ type:'warn', text:'⚠️ Fal.ai sin créditos — se usará Pexels.' });
      }
      onSave();
      if (!aprobar) onClose();
    } catch { setMsg({ type:'err', text:'Error de conexión' }); }
    setSaving(false);
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
         onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'#1a1d2e', border:'1px solid #2a2e4a', borderRadius:14, padding:28, width:'100%', maxWidth:620, maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ color:'#f0f0ff', fontSize:16 }}>✏️ Editar borrador</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#9b9fc4', fontSize:20, cursor:'pointer' }}>✕</button>
        </div>

        <Input label="Título" value={titulo} onChange={setTitulo} placeholder="Título del post" />
        <Input label="Contenido" value={contenido} onChange={setContenido} rows={7}
               placeholder="Texto completo del post listo para publicar..." />

        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block', color:'#9b9fc4', fontSize:13, marginBottom:8 }}>🖼️ Fuente de imagen</label>
          <div style={{ display:'flex', gap:8, marginBottom:12 }}>
            {[['ia','🤖 IA (Fal.ai)'],['pexels','📷 Pexels'],['drive','☁️ Drive']].map(([val,lbl]) => (
              <button key={val} onClick={() => setImagenFuente(val)} style={{
                flex:1, padding:'9px 0', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:13,
                background: imagenFuente===val ? '#3b0764' : '#12141f',
                border: `1px solid ${imagenFuente===val ? '#7c3aed' : '#2a2e4a'}`,
                color: imagenFuente===val ? '#e9d5ff' : '#9b9fc4',
              }}>{lbl}</button>
            ))}
          </div>
          {imagenFuente === 'ia' && (
            <Input label="Prompt de imagen (en inglés)" value={imagenPrompt} onChange={setImagenPrompt}
                   placeholder="Ej: modern office interior, laptop, purple tones, flat design" />
          )}
          {imagenFuente === 'pexels' && (
            <Input label="Keyword para Pexels (en inglés)" value={imagenNombre} onChange={setImagenNombre}
                   placeholder="Ej: modern apartment interior" />
          )}
          {imagenFuente === 'drive' && (
            <Input label="Nombre del archivo en Drive" value={imagenNombre} onChange={setImagenNombre}
                   placeholder="Ej: banner_mayo.jpg" />
          )}
        </div>

        <Input label="Fecha de publicación" value={fecha} onChange={setFecha} type="datetime-local" />

        {msg && (
          <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:13,
            background: msg.type==='ok' ? '#1a3a2a' : msg.type==='warn' ? '#3a2e1a' : '#3a1a1a',
            color: msg.type==='ok' ? '#4ade80' : msg.type==='warn' ? '#fbbf24' : '#f87171',
            border: `1px solid ${msg.type==='ok' ? '#2a5a3a' : msg.type==='warn' ? '#5a4a2a' : '#5a2a2a'}` }}>
            {msg.text}
          </div>
        )}

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Btn>
          <Btn variant="secondary" onClick={() => handleSave(false)} disabled={saving}>
            {saving ? '⏳...' : '💾 Guardar borrador'}
          </Btn>
          <Btn variant="teal" onClick={() => handleSave(true)} disabled={saving}>
            {saving ? '⏳...' : '✅ Guardar y aprobar'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function TabPlan() {
  const [contexto,      setContexto]      = useState(null);
  const [loadingCtx,    setLoadingCtx]    = useState(true);
  const [fuenteForm,    setFuenteForm]    = useState('url');
  const [url,           setUrl]           = useState('');
  const [descripcion,   setDescripcion]   = useState('');
  const [onboarding,    setOnboarding]    = useState({ que_haces:'', que_vendes:'', cliente_ideal:'', diferenciador:'', tono:'profesional' });
  const [savingCtx,     setSavingCtx]     = useState(false);
  const [msgCtx,        setMsgCtx]        = useState(null);

  const now = new Date();
  const [mes,      setMes]      = useState(now.getMonth() + 1);
  const [anio,     setAnio]     = useState(now.getFullYear());
  const [numPosts, setNumPosts] = useState(20);
  const [redes,    setRedes]    = useState(['linkedin', 'facebook', 'instagram']);
  const [generando, setGenerando] = useState(false);
  const [msgPlan,   setMsgPlan]   = useState(null);

  // CTA y catálogo
  const [opciones,          setOpciones]          = useState(null);
  const [ctaTipo,           setCtaTipo]           = useState('auto');
  const [ctaValor,          setCtaValor]          = useState('');
  const [incluirCatalogo,   setIncluirCatalogo]   = useState(false);

  const [planes,       setPlanes]       = useState([]);
  const [loadingPlanes, setLoadingPlanes] = useState(true);
  const [planActivo,   setPlanActivo]   = useState(null);
  const [borradores,   setBorradores]   = useState([]);
  const [aprobandoTodo, setAprobandoTodo] = useState(false);
  const [editando,     setEditando]     = useState(null);  // post a editar

  const cargarContexto = useCallback(async () => {
    setLoadingCtx(true);
    try {
      const r = await fetch(`${API}/social/context`, { headers: authHeaders() });
      if (r.ok) { const d = await r.json(); setContexto(d.tiene_contexto ? d.contexto : null); }
    } catch {}
    setLoadingCtx(false);
  }, []);

  const cargarPlanes = useCallback(async () => {
    setLoadingPlanes(true);
    try {
      const r = await fetch(`${API}/social/plan`, { headers: authHeaders() });
      if (r.ok) setPlanes(await r.json());
    } catch {}
    setLoadingPlanes(false);
  }, []);

  const cargarBorradores = useCallback(async (planId) => {
    try {
      const r = await fetch(`${API}/social/scheduled?estado=borrador`, { headers: authHeaders() });
      if (r.ok) {
        const all = await r.json();
        setBorradores(planId ? all.filter(b => b.plan_id === planId) : all);
      }
    } catch {}
  }, []);

  useEffect(() => { cargarContexto(); cargarPlanes(); }, [cargarContexto, cargarPlanes]);

  useEffect(() => {
    fetch(`${API}/social/plan/options`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setOpciones(d); if (!d.tiene_whatsapp) setCtaTipo('web'); } })
      .catch(() => {});
  }, []);

  async function handleGuardarContexto() {
    setSavingCtx(true); setMsgCtx(null);
    const payload = {};
    if (fuenteForm === 'url') payload.url = url.trim();
    else if (fuenteForm === 'descripcion') payload.descripcion = descripcion.trim();
    else payload.respuestas_onboarding = onboarding;
    try {
      const r = await fetch(`${API}/social/context/analyze`, { method:'POST', headers: authHeaders(), body: JSON.stringify(payload) });
      const d = await r.json();
      if (r.ok) { setMsgCtx({ type:'ok', text:`✅ Contexto guardado (${d.tokens_used} tokens)` }); cargarContexto(); }
      else setMsgCtx({ type:'err', text: d.detail || 'Error analizando' });
    } catch { setMsgCtx({ type:'err', text:'Error de conexión' }); }
    setSavingCtx(false);
  }

  async function handleGenerarPlan() {
    if (!contexto) return;
    setGenerando(true); setMsgPlan(null);
    try {
      const r = await fetch(`${API}/social/plan/generate`, {
        method:'POST', headers: authHeaders(),
        body: JSON.stringify({
          mes, anio, num_posts: numPosts, redes_activas: redes,
          cta_tipo: ctaTipo,
          cta_valor: ctaValor.trim() || null,
          incluir_catalogo: incluirCatalogo,
        }),
      });
      const d = await r.json();
      if (r.ok) {
        setMsgPlan({ type:'ok', text:`✅ Plan generado — ${d.borradores_creados || d.total_posts} borradores listos para revisar` });
        cargarPlanes();
        const rPlan = await fetch(`${API}/social/plan/${d.id}`, { headers: authHeaders() });
        if (rPlan.ok) { const pd = await rPlan.json(); setPlanActivo(pd); cargarBorradores(d.id); }
      } else setMsgPlan({ type:'err', text: d.detail || 'Error generando el plan' });
    } catch { setMsgPlan({ type:'err', text:'Error de conexión' }); }
    setGenerando(false);
  }

  async function handleVerPlan(planId) {
    try {
      const r = await fetch(`${API}/social/plan/${planId}`, { headers: authHeaders() });
      if (r.ok) { const d = await r.json(); setPlanActivo(d); cargarBorradores(planId); }
    } catch {}
  }

  async function handleEliminarPost(planId, postId) {
    try {
      await fetch(`${API}/social/plan/${planId}/post/${postId}`, { method:'DELETE', headers: authHeaders() });
      handleVerPlan(planId);
    } catch {}
  }

  async function handleAprobarTodo() {
    if (!planActivo) return;
    setAprobandoTodo(true);
    try {
      const r = await fetch(`${API}/social/plan/${planActivo.id}/approve`, {
        method:'POST', headers: authHeaders(), body: JSON.stringify({ ajustes: null }),
      });
      const d = await r.json();
      if (r.ok) {
        setMsgPlan({
          type: d.fal_sin_credito ? 'warn' : 'ok',
          text: d.mensaje,
        });
        cargarPlanes(); handleVerPlan(planActivo.id);
      } else setMsgPlan({ type:'err', text: d.detail || 'Error aprobando' });
    } catch { setMsgPlan({ type:'err', text:'Error de conexión' }); }
    setAprobandoTodo(false);
  }

  async function handleAprobarUno(postId) {
    try {
      const r = await fetch(`${API}/social/scheduled/${postId}/approve`, { method:'POST', headers: authHeaders() });
      const d = await r.json();
      if (r.ok) {
        if (d.fal_sin_credito) setMsgPlan({ type:'warn', text:'⚠️ Fal.ai sin créditos — se usará Pexels para este post.' });
        if (planActivo) cargarBorradores(planActivo.id);
      }
    } catch {}
  }

  const platformIcons = { linkedin:'💼', facebook:'📘', instagram:'📸' };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* Contexto del negocio */}
      <Card>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
          <div>
            <h3 style={{ color:'#f0f0ff', fontSize:16, marginBottom:4 }}>🏢 Contexto de tu negocio</h3>
            <p style={{ color:'#5a5e82', fontSize:13 }}>La IA necesita conocer tu negocio para generar contenido relevante.</p>
          </div>
          {!loadingCtx && contexto && <Badge color="green">✓ Configurado</Badge>}
        </div>
        {!loadingCtx && contexto && (
          <div style={{ background:'#12141f', border:'1px solid #2a5a3a', borderRadius:10, padding:16, marginBottom:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
              <span style={{ color:'#4ade80', fontWeight:700, fontSize:13 }}>{contexto.nombre_negocio || 'Tu negocio'}</span>
              <Badge color="blue">{contexto.sector}</Badge>
            </div>
            <p style={{ color:'#9b9fc4', fontSize:13 }}>{contexto.descripcion_corta}</p>
          </div>
        )}
        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block', color:'#9b9fc4', fontSize:13, marginBottom:8 }}>
            {contexto ? 'Actualizar contexto:' : '¿Cómo conocemos tu negocio?'}
          </label>
          <div style={{ display:'flex', gap:8, marginBottom:16 }}>
            {[['url','🌐 Mi web'],['descripcion','✍️ Lo describo'],['onboarding','📋 Formulario']].map(([id,lbl]) => (
              <button key={id} onClick={() => setFuenteForm(id)} style={{
                flex:1, padding:'9px 0', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:13,
                background: fuenteForm===id ? '#3b0764' : '#12141f',
                border: `1px solid ${fuenteForm===id ? '#7c3aed' : '#2a2e4a'}`,
                color: fuenteForm===id ? '#e9d5ff' : '#9b9fc4',
              }}>{lbl}</button>
            ))}
          </div>
          {fuenteForm === 'url' && <Input value={url} onChange={setUrl} placeholder="https://tunegocio.com" label="URL de tu web" />}
          {fuenteForm === 'descripcion' && <Input value={descripcion} onChange={setDescripcion} rows={4} label="Describe tu negocio" placeholder="Ej: Somos una clínica dental en Madrid..." />}
          {fuenteForm === 'onboarding' && (
            <>
              <Input label="¿A qué se dedica?" value={onboarding.que_haces} onChange={v => setOnboarding(o=>({...o,que_haces:v}))} placeholder="Ej: Clínica dental con 3 sedes en Madrid" />
              <Input label="¿Qué ofreces?" value={onboarding.que_vendes} onChange={v => setOnboarding(o=>({...o,que_vendes:v}))} placeholder="Ej: Ortodoncia, blanqueamiento, implantes" />
              <Input label="¿Cliente ideal?" value={onboarding.cliente_ideal} onChange={v => setOnboarding(o=>({...o,cliente_ideal:v}))} placeholder="Ej: Adultos 30-50 años, profesionales" />
              <Input label="¿Qué te diferencia?" value={onboarding.diferenciador} onChange={v => setOnboarding(o=>({...o,diferenciador:v}))} placeholder="Ej: Tratamientos express sin listas de espera" />
              <Select label="Tono" value={onboarding.tono} onChange={v => setOnboarding(o=>({...o,tono:v}))}
                options={[{value:'profesional',label:'Profesional'},{value:'cercano',label:'Cercano'},{value:'inspirador',label:'Inspirador'},{value:'divertido',label:'Divertido'},{value:'tecnico',label:'Técnico'}]} />
            </>
          )}
        </div>
        {msgCtx && <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:13,
          background: msgCtx.type==='ok' ? '#1a3a2a' : '#3a1a1a', color: msgCtx.type==='ok' ? '#4ade80' : '#f87171',
          border: `1px solid ${msgCtx.type==='ok' ? '#2a5a3a' : '#5a2a2a'}` }}>{msgCtx.text}</div>}
        <Btn onClick={handleGuardarContexto} disabled={savingCtx}>
          {savingCtx ? '⏳ Analizando...' : contexto ? '🔄 Actualizar' : '🔍 Analizar y guardar'}
        </Btn>
      </Card>

      {/* Generar plan */}
      <Card style={{ opacity: contexto ? 1 : 0.5 }}>
        <h3 style={{ color:'#f0f0ff', fontSize:16, marginBottom:4 }}>📅 Generar plan mensual</h3>
        <p style={{ color:'#5a5e82', fontSize:13, marginBottom:20 }}>
          {contexto ? 'La IA generará el contenido completo de cada post. Podrás revisar y editar antes de aprobar.' : '⚠️ Configura primero el contexto.'}
        </p>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:16 }}>
          <Select label="Mes" value={mes} onChange={v => setMes(Number(v))} options={MESES.map((m,i) => ({ value:i+1, label:m }))} />
          <Select label="Año" value={anio} onChange={v => setAnio(Number(v))} options={[2025,2026,2027].map(y=>({value:y,label:y}))} />
          <Select label="Posts" value={numPosts} onChange={v => setNumPosts(Number(v))} options={[10,12,15,20,24,28,30].map(n=>({value:n,label:`${n} posts`}))} />
        </div>
        <PlatformToggle selected={redes} onChange={setRedes} />

        {/* CTA */}
        <div style={{ marginBottom:16 }}>
          <label style={{ display:'block', color:'#9b9fc4', fontSize:13, marginBottom:8 }}>
            📣 CTA de los posts — ¿a dónde lleva la llamada a la acción?
          </label>
          <div style={{ display:'flex', gap:8, marginBottom:10, flexWrap:'wrap' }}>
            {[
              ['auto',    '⚡ Automático'],
              ['whatsapp','💬 WhatsApp'],
              ['web',     '🌐 Mi web'],
              ['link',    '🔗 Link propio'],
              ['ninguno', '🚫 Sin CTA'],
            ].map(([val, lbl]) => {
              const disabled = val === 'whatsapp' && opciones && !opciones.tiene_whatsapp;
              return (
                <button key={val} onClick={() => !disabled && setCtaTipo(val)} style={{
                  padding:'7px 14px', borderRadius:7, cursor: disabled ? 'not-allowed' : 'pointer',
                  fontWeight:600, fontSize:12, opacity: disabled ? 0.4 : 1,
                  background: ctaTipo===val ? '#3b0764' : '#12141f',
                  border: `1px solid ${ctaTipo===val ? '#7c3aed' : '#2a2e4a'}`,
                  color: ctaTipo===val ? '#e9d5ff' : '#9b9fc4',
                }} title={disabled ? 'Requiere plan con WhatsApp' : ''}>
                  {lbl}
                </button>
              );
            })}
          </div>
          {ctaTipo === 'auto' && (
            <p style={{ color:'#5a5e82', fontSize:12 }}>
              {opciones?.tiene_whatsapp
                ? `✓ Usará WhatsApp: wa.me/${opciones.whatsapp_numero}`
                : '⚠️ Sin WhatsApp configurado — los posts no incluirán CTA de contacto'}
            </p>
          )}
          {(ctaTipo === 'web' || ctaTipo === 'link') && (
            <input value={ctaValor} onChange={e => setCtaValor(e.target.value)}
              placeholder={ctaTipo==='web' ? 'https://tunegocio.com' : 'Link o número de contacto'}
              style={{ width:'100%', background:'#12141f', border:'1px solid #2a2e4a',
                borderRadius:8, padding:'9px 14px', color:'#f0f0ff', fontSize:13,
                outline:'none', boxSizing:'border-box' }} />
          )}
          {ctaTipo === 'whatsapp' && opciones?.tiene_whatsapp && (
            <p style={{ color:'#5a5e82', fontSize:12 }}>✓ wa.me/{opciones.whatsapp_numero}</p>
          )}
        </div>

        {/* Catálogo */}
        {opciones?.tiene_catalogo && (
          <div style={{ marginBottom:16, padding:'12px 16px', borderRadius:8,
            background: incluirCatalogo ? 'rgba(124,77,255,0.08)' : '#12141f',
            border: `1px solid ${incluirCatalogo ? 'rgba(124,77,255,0.4)' : '#2a2e4a'}` }}>
            <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
              <input type="checkbox" checked={incluirCatalogo} onChange={e => setIncluirCatalogo(e.target.checked)}
                style={{ width:16, height:16, cursor:'pointer' }} />
              <div>
                <span style={{ color:'#f0f0ff', fontSize:13, fontWeight:600 }}>
                  {opciones.catalogo_tipo === 'inmobiliaria' ? '🏠 Incluir propiedades en el plan' : '🛍️ Incluir productos en el plan'}
                </span>
                <p style={{ color:'#5a5e82', fontSize:12, margin:'2px 0 0' }}>
                  {opciones.catalogo_tipo === 'inmobiliaria'
                    ? 'El plan incluirá posts con pisos y propiedades reales de tu cartera'
                    : 'El plan incluirá posts con productos concretos de tu catálogo'}
                </p>
              </div>
            </label>
          </div>
        )}
        {msgPlan && <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:13,
          background: msgPlan.type==='ok' ? '#1a3a2a' : msgPlan.type==='warn' ? '#3a2e1a' : '#3a1a1a',
          color: msgPlan.type==='ok' ? '#4ade80' : msgPlan.type==='warn' ? '#fbbf24' : '#f87171',
          border:`1px solid ${msgPlan.type==='ok' ? '#2a5a3a' : msgPlan.type==='warn' ? '#5a4a2a' : '#5a2a2a'}` }}>{msgPlan.text}</div>}
        <Btn onClick={handleGenerarPlan} disabled={generando || !contexto || redes.length===0}>
          {generando ? '⏳ Generando contenido completo...' : '✨ Generar plan del mes'}
        </Btn>
      </Card>

      {/* Lista de planes */}
      {!loadingPlanes && planes.length > 0 && (
        <Card>
          <h3 style={{ color:'#f0f0ff', fontSize:16, marginBottom:16 }}>📋 Mis planes</h3>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {planes.map(p => (
              <div key={p.id} style={{
                background:'#12141f', border:`1px solid ${planActivo?.id===p.id ? '#7c3aed' : '#2a2e4a'}`,
                borderRadius:8, padding:16, display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer',
              }} onClick={() => handleVerPlan(p.id)}>
                <div>
                  <div style={{ display:'flex', gap:8, marginBottom:6, alignItems:'center' }}>
                    <span style={{ color:'#f0f0ff', fontWeight:700, fontSize:14 }}>{p.mes} {p.anio}</span>
                    <Badge color={p.estado==='aprobado' ? 'green' : p.estado==='borrador' ? 'yellow' : 'gray'}>{p.estado}</Badge>
                    <span style={{ color:'#5a5e82', fontSize:12 }}>{p.total_posts} posts</span>
                  </div>
                  {p.resumen_estrategia && <p style={{ color:'#9b9fc4', fontSize:12 }}>{p.resumen_estrategia.slice(0,100)}...</p>}
                </div>
                <span style={{ color:'#5a5e82', fontSize:18 }}>›</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Borradores del plan activo */}
      {planActivo && (
        <Card>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
            <div>
              <h3 style={{ color:'#f0f0ff', fontSize:16, marginBottom:4 }}>
                {planActivo.plan?.mes} {planActivo.plan?.anio}
              </h3>
              {planActivo.plan?.resumen_estrategia && (
                <p style={{ color:'#9b9fc4', fontSize:13 }}>{planActivo.plan.resumen_estrategia}</p>
              )}
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <Badge color={planActivo.estado==='aprobado' ? 'green' : planActivo.estado==='borrador' ? 'yellow' : 'gray'}>
                {planActivo.estado}
              </Badge>
              {(planActivo.estado === 'borrador' || borradores.length > 0) && (
                <Btn variant="teal" onClick={handleAprobarTodo} disabled={aprobandoTodo}>
                  {aprobandoTodo ? '⏳ Aprobando...' : `✅ Aprobar todos (${borradores.length})`}
                </Btn>
              )}
            </div>
          </div>

          {/* Lista de borradores */}
          {borradores.length > 0 ? (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {borradores.map(b => (
                <div key={b.id} style={{ background:'#12141f', border:'1px solid #2a2e4a', borderRadius:10, padding:16 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                    <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                      <span style={{ color:'#f0f0ff', fontWeight:700, fontSize:13 }}>
                        {b.plataformas?.map(p => platformIcons[p] || p).join(' ')}
                        {' '}{new Date(b.programado_para).toLocaleDateString('es-ES', { day:'numeric', month:'short' })}
                      </span>
                      <Badge color={OBJETIVO_COLOR['awareness'] || 'gray'}>{b.plataformas?.[0]}</Badge>
                      {b.editado && <Badge color="yellow">✏️ editado</Badge>}
                      <span style={{ color:'#5a5e82', fontSize:11 }}>
                        {b.imagen_fuente==='ia' ? '🤖 IA' : b.imagen_fuente==='drive' ? '☁️ Drive' : '📷 Pexels'}
                      </span>
                    </div>
                    <div style={{ display:'flex', gap:6 }}>
                      <button onClick={() => setEditando(b)} style={{
                        background:'#1a1d2e', border:'1px solid #2a2e4a', borderRadius:6, padding:'5px 12px',
                        color:'#9b9fc4', cursor:'pointer', fontSize:12, fontWeight:600 }}>✏️ Editar</button>
                      <button onClick={() => handleAprobarUno(b.id)} style={{
                        background:'#0f3a35', border:'1px solid #1a5a50', borderRadius:6, padding:'5px 12px',
                        color:'#2dd4bf', cursor:'pointer', fontSize:12, fontWeight:600 }}>✓ Aprobar</button>
                    </div>
                  </div>
                  {b.titulo && <p style={{ color:'#c4b5fd', fontSize:13, fontWeight:600, marginBottom:6 }}>{b.titulo}</p>}
                  <p style={{ color:'#9b9fc4', fontSize:13, lineHeight:1.5 }}>
                    {b.contenido?.slice(0,180)}{b.contenido?.length > 180 ? '...' : ''}
                  </p>
                </div>
              ))}
            </div>
          ) : planActivo.estado === 'aprobado' ? (
            <div style={{ background:'#1a3a2a', border:'1px solid #2a5a3a', borderRadius:8, padding:16, textAlign:'center', color:'#4ade80', fontSize:13 }}>
              ✓ Plan aprobado — todos los posts están programados para publicación automática
            </div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid #2a2e4a' }}>
                    {['Día','Red','Objetivo','Título','Imagen',''].map(h => (
                      <th key={h} style={{ textAlign:'left', padding:'8px 12px', color:'#5a5e82', fontWeight:600, fontSize:11, textTransform:'uppercase' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(planActivo.plan?.posts || []).map((post, idx) => (
                    <tr key={post.id} style={{ borderBottom:'1px solid #1a1d2e', background: idx%2===0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                      <td style={{ padding:'10px 12px', color:'#f0f0ff', fontWeight:700 }}>{post.dia}</td>
                      <td style={{ padding:'10px 12px' }}>{RED_ICON[post.red]||'📡'} {post.red}</td>
                      <td style={{ padding:'10px 12px' }}><Badge color={OBJETIVO_COLOR[post.objetivo]||'gray'}>{post.objetivo}</Badge></td>
                      <td style={{ padding:'10px 12px', color:'#9b9fc4', maxWidth:200 }}>{post.titulo}</td>
                      <td style={{ padding:'10px 12px', color:'#5a5e82', fontSize:12 }}>{post.imagen_sugerida?.slice(0,20) || '—'}</td>
                      <td style={{ padding:'10px 12px' }}>
                        {planActivo.estado === 'borrador' && (
                          <button onClick={() => handleEliminarPost(planActivo.id, post.id)}
                            style={{ background:'transparent', border:'none', cursor:'pointer', color:'#5a5e82', fontSize:16 }}>✕</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Modal de edición */}
      {editando && (
        <ModalEditarBorrador
          post={editando}
          onClose={() => setEditando(null)}
          onSave={() => { setEditando(null); if (planActivo) cargarBorradores(planActivo.id); }}
        />
      )}
    </div>
  );
}


// ─── Página principal ────────────────────────────────────────────────────────

export default function Social() {
  const [tab, setTab]         = useState('generar');
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);

  const cargarStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API}/social/status`, { headers: authHeaders() });
      if (r.ok) setStatus(await r.json());
      else if (r.status === 403) setStatus({ modulo_social: false });
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { cargarStatus(); }, [cargarStatus]);

  const tabs = [
    { id: 'generar',      label: '✨ Generar & Publicar' },
    { id: 'programar',    label: '📅 Programar' },
    { id: 'plan',         label: '🗓️ Plan de Contenido' },   // ← NUEVO
    { id: 'historial',    label: '📋 Historial' },
    { id: 'credenciales', label: '🔑 Credenciales' },
  ];

  if (loading) {
    return (
      <div style={{ padding: 32, color: '#9b9fc4', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span>⏳</span> Cargando módulo social...
      </div>
    );
  }

  if (!status?.modulo_social) {
    return (
      <div style={{ padding: 32, maxWidth: 560 }}>
        <h1 style={{ color: '#f0f0ff', fontSize: 24, fontWeight: 800, marginBottom: 8 }}>
          📣 Publicador Social
        </h1>
        <p style={{ color: '#5a5e82', marginBottom: 32 }}>
          Genera y publica contenido en LinkedIn, Facebook e Instagram con IA.
        </p>
        <Card>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
            <h2 style={{ color: '#f0f0ff', fontSize: 20, marginBottom: 12 }}>Módulo no activado</h2>
            <p style={{ color: '#9b9fc4', marginBottom: 24, fontSize: 14 }}>
              El Publicador Social está disponible en los planes Web+Social, WhatsApp+Social y Completo+Social.
            </p>
            <Btn onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'billing' }))}>
              Ver planes y precios
            </Btn>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ color: '#f0f0ff', fontSize: 24, fontWeight: 800, marginBottom: 6 }}>
            📣 Publicador Social
          </h1>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {status.linkedin_conectado  && <Badge color="blue">💼 LinkedIn</Badge>}
            {status.facebook_conectado  && <Badge color="blue">📘 Facebook</Badge>}
            {status.instagram_conectado && <Badge color="blue">📸 Instagram</Badge>}
            {status.drive_conectado
              ? <Badge color="green">☁️ Drive{status.drive_carpeta ? ' + Carpeta' : ''}</Badge>
              : <Badge color="yellow">☁️ Drive no conectado</Badge>
            }
            {!status.linkedin_conectado && !status.facebook_conectado && !status.instagram_conectado && (
              <Badge color="yellow">⚠️ Sin redes conectadas — ve a Credenciales</Badge>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #2a2e4a' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '10px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 14,
            background: 'transparent', border: 'none',
            borderBottom: `2px solid ${tab === t.id ? '#7c3aed' : 'transparent'}`,
            color: tab === t.id ? '#c4b5fd' : '#9b9fc4', marginBottom: -1,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'generar'      && <TabGenerar status={status} />}
      {tab === 'programar'    && <TabProgramar status={status} />}
      {tab === 'plan'         && <TabPlan />}
      {tab === 'historial'    && <TabHistorial />}
      {tab === 'credenciales' && <TabCredenciales status={status} onRefresh={cargarStatus} />}
    </div>
  );
}